import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Ban, Eye, EyeOff, Loader2, Plus, RefreshCw, Settings as SettingsIcon, ShieldOff, Trash2 } from 'lucide-react';
import { useAccountStore } from '../../stores/accountStore';
import { usePolyAccountStore } from '../../stores/polyAccountStore';
import { isValidEvmAddress, isValidEvmPrivateKey } from '../../lib/polymarket/client';
import { apiFetch, useAuthStore } from '../../stores/authStore';
import { useTheme } from '../../contexts/ThemeContext';
import { hasStoredTheme, type Theme } from '../../lib/infra/themeManager';
import { useOscarAccess } from '../../hooks/useOscarAccess';
import { useLt2Access } from '../../hooks/useLt2Access';
import { ConfirmModal } from '../../components/atoms/Modal';
import {
  CredentialHealthLines,
  CredentialStateBadge,
  type CredentialLifecycle,
  getCredentialState,
  isCredentialEnabled,
} from '../../components/credential/CredentialLifecycle';
import {
  useDisplaySettings,
  type DashboardMarketType,
  type DefaultExpiryMode,
  type PositionViewMode,
  type SurfaceViewMode,
  type DashboardSortKey,
  type DashboardSortDir,
  type PositionsOrderSortKey,
  type PositionsOrderSortDir,
  type BotBoardViewMode,
  type BotBoardSortKey,
  type MarketSectionViewMode,
  type RestingOrderDisplay,
  INLINE_CHIP_LIMIT,
} from '../../stores/displaySettings';
import { SPORT_TABS, type Sport, type PriceMode, type SlateRange } from '../ValueDashboard/types';
import { AlertSettingsPanel } from '../../components/organisms/AlertSettingsPanel';
import { BotDefaultsPanel } from './BotDefaultsPanel';
import { QuickSizeSlots } from './QuickSizeSlots';
import { scrollToHashSection } from './scrollToHashSection';
import { kalshiReportedBalanceTitle } from '../../lib/kalshiBalance';

interface StoredCredential extends CredentialLifecycle {
  id: string;
  label: string;
  accessKey: string;
  venue: string;
  environment: string;
  createdAt: string;
}

export function Settings() {
  const status = useAccountStore((s) => s.status);
  const error = useAccountStore((s) => s.error);
  const balance = useAccountStore((s) => s.balance);
  const keyMode = useAccountStore((s) => s.keyMode);
  const connect = useAccountStore((s) => s.connect);
  const disconnect = useAccountStore((s) => s.disconnect);
  const checkCredentials = useAccountStore((s) => s.checkCredentials);

  // Personal-key Polymarket trading is disabled for trader-role users in
  // this app — see PolyPersonalKeyClearedBanner + AuthedShell central mount.
  // We hide the form here so traders can't see the disabled control either.
  const role = useAuthStore((s) => s.user?.role);
  // Not for gating — `BotDefaultsPanel` reads this itself. It is a re-anchor
  // trigger: the panel renders null until the flag arrives, and it sits ABOVE
  // `#alerts`. See the deep-link effect below.
  const botAccess = useAuthStore((s) => s.user?.botAccess ?? false);
  const personalPolyHidden = role === 'trader';

  const [credentials, setCredentials] = useState<StoredCredential[]>([]);
  const [loadingCreds, setLoadingCreds] = useState(true);
  const [credsError, setCredsError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [actingCredentialId, setActingCredentialId] = useState<string | null>(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<StoredCredential | null>(null);

  const enabledCredentialCount = credentials.filter(isCredentialEnabled).length;

  const fetchCredentials = useCallback(async () => {
    setLoadingCreds(true);
    setCredsError(null);
    try {
      const res = await apiFetch('/api/trader/credentials');
      if (res.ok) {
        const data = await res.json();
        setCredentials(data.credentials ?? []);
      } else {
        setCredsError(`Failed to load keys (${res.status}). Try refreshing.`);
      }
    } catch {
      setCredsError('Could not reach the server. Try refreshing.');
    }
    setLoadingCreds(false);
  }, []);

  useEffect(() => { fetchCredentials(); }, [fetchCredentials]);

  const handleAddKey = useCallback(async (key: {
    label: string;
    accessKey: string;
    privateKeyPem: string;
    environment: 'prod' | 'demo';
  }) => {
    setActionError(null);
    const res = await apiFetch('/api/trader/credentials', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(key),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: StoredCredential;
      error?: string;
    };
    if (!res.ok || !data.credential) {
      setActionError(data.error ?? `Failed to save key (${res.status}).`);
      return false;
    }
    setCredentials((prev) => [...prev, data.credential!]);
    setShowAddForm(false);
    await checkCredentials();
    if (status === 'disconnected' || status === 'error') {
      void connect();
    }
    return true;
  }, [status, connect, checkCredentials]);

  const handlePatchCredential = useCallback(async (
    credential: StoredCredential,
    body: { disabled?: boolean; release?: boolean },
  ) => {
    setActionError(null);
    setActingCredentialId(credential.id);
    const res = await apiFetch(`/api/trader/credentials/${credential.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({})) as {
      credential?: StoredCredential;
      error?: string;
    };
    if (!res.ok || !data.credential) {
      setActionError(data.error ?? `Failed to update key (${res.status}).`);
      setActingCredentialId(null);
      return;
    }
    const nextCredentials = credentials.map((c) => (c.id === credential.id ? data.credential! : c));
    setCredentials(nextCredentials);
    if (nextCredentials.length === 0) disconnect();
    await checkCredentials();
    if (nextCredentials.filter(isCredentialEnabled).length === 0) disconnect();
    setActingCredentialId(null);
  }, [credentials, disconnect, checkCredentials]);

  const handleHardDeleteKey = useCallback(async () => {
    if (!hardDeleteTarget) return;
    const target = hardDeleteTarget;
    setHardDeleteTarget(null);
    setActionError(null);
    setActingCredentialId(target.id);
    const res = await apiFetch(
      `/api/trader/credentials/${target.id}?hard=true&confirm=delete`,
      { method: 'DELETE' },
    );
    const data = await res.json().catch(() => ({})) as { error?: string };
    if (!res.ok) {
      setActionError(data.error ?? `Failed to delete key (${res.status}).`);
      setActingCredentialId(null);
      return;
    }
    const nextCredentials = credentials.filter((c) => c.id !== target.id);
    setCredentials(nextCredentials);
    if (nextCredentials.filter(isCredentialEnabled).length === 0) disconnect();
    await checkCredentials();
    setActingCredentialId(null);
  }, [credentials, disconnect, checkCredentials, hardDeleteTarget]);

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  // Deep link from #234's pull-up (`/settings#alerts`).
  //
  // Re-anchored when an async section above the target settles, not just on
  // the first commit. The node exists immediately — but EXISTING is not the
  // same as SETTLED: a section ABOVE the anchor that grows after the scroll
  // leaves the reader somewhere other than where they were sent.
  //
  // Which sections those are changed with #677's reordering, and the deps are
  // the list:
  //
  //   `loadingCreds` — pre-#677 the Kalshi card sat above `#alerts` and grew
  //     by a row per stored key when the fetch resolved. It is now the LAST
  //     group, so nothing it does displaces an anchor above it (`#account` is
  //     the card itself, and a card's anchor is its top edge — the growth
  //     happens below). Kept as insurance for any future section that loads
  //     above an anchor, not because it currently corrects anything.
  //
  //   `botAccess` — the displacement #677 actually created. `BotDefaultsPanel`
  //     renders null until the flag arrives and now sits ABOVE `#alerts`, and
  //     `LimitsWidget` refreshes the user on every app mount — so a cold
  //     `/settings#alerts` load whose persisted flag lags the fresh one pops
  //     the panel in after the scroll and pushes the target down.
  //
  // The cost, stated because it is not free: each re-anchor calls `focus()`,
  // so a trader who tabbed into a control while one of these settled loses
  // focus to the section. The window is short and the alternative — landing a
  // deep link in the wrong place — is worse, but it is why the deps are a
  // named list rather than every value on the page.
  const { hash } = useLocation();
  useEffect(() => {
    scrollToHashSection(hash);
  }, [hash, loadingCreds, botAccess]);

  return (
    <div className="h-full overflow-auto p-6">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <SettingsIcon size={24} className="text-muted-foreground" />
          <h1 className="text-xl font-semibold">Settings</h1>
        </div>

        {/* Groups 1–5 of #677's seven: the browser-local preference cards.
            One component so the panel stays renderable on its own (the lt2
            boundary test drives it directly), five cards so the groups read
            as peers of Alerts and Account below. */}
        <PreferencesPanel />

        {/* Bot defaults — renders only when the trader has bot access */}
        <BotDefaultsPanel />

        {/* Alerts & Notifications — the canonical home for every emitted sound
            (#232). The id + tabIndex are the deep-link target for #234's
            Position Tracker pull-up; the panel itself stays prop-free. */}
        <section id="alerts" tabIndex={-1} className="scroll-mt-2 focus:outline-none">
          <AlertSettingsPanel />
        </section>

        {/* Kalshi Account Card — and the head of #677's group 7, "Account and
            credentials", so it carries the group's deep-link anchor. The id
            sits on the existing card rather than on a wrapper around both
            venue cards: a wrapper would swallow them into one child of the
            page's `space-y-6` and collapse the gap between them. */}
        <section
          id="account"
          tabIndex={-1}
          className="bg-card border-border scroll-mt-2 rounded-lg border p-5 focus:outline-none"
        >
          <div className="mb-5 flex items-center justify-between">
            <h2 className="text-base font-medium">Kalshi Credentials</h2>
            <StatusBadge status={status} balance={balance} keyMode={keyMode} />
          </div>

          {/* Stored Keys */}
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">
                Stored Keys ({credentials.length === 0 ? 'none' : `${enabledCredentialCount}/${credentials.length} enabled`})
              </label>
              <button onClick={fetchCredentials} title="Refresh"
                className="text-muted-foreground hover:text-foreground rounded p-1">
                <RefreshCw size={12} />
              </button>
            </div>

            {loadingCreds ? (
              <div className="flex justify-center py-4">
                <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
              </div>
            ) : credsError ? (
              <div className="flex items-center justify-between rounded-md bg-red-500/10 px-3 py-2">
                <p className="text-xs text-red-500 dark:text-red-400">{credsError}</p>
                <button onClick={fetchCredentials}
                  className="ml-2 flex items-center gap-1 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-500/10 dark:text-red-400">
                  <RefreshCw size={10} /> Retry
                </button>
              </div>
            ) : credentials.length > 0 ? (
              <div className="space-y-1.5">
                {credentials.map((cred) => (
                  <CredentialRow
                    key={cred.id}
                    credential={cred}
                    acting={actingCredentialId === cred.id}
                    onRelease={() => handlePatchCredential(cred, { release: true })}
                    onToggleDisabled={() => handlePatchCredential(
                      cred,
                      { disabled: isCredentialEnabled(cred) },
                    )}
                    onHardDelete={() => setHardDeleteTarget(cred)}
                  />
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">No keys stored. Add a key to connect.</p>
            )}
          </div>

          {/* Add Key */}
          {showAddForm ? (
            <AddKeyForm onAdd={handleAddKey} onCancel={() => setShowAddForm(false)} />
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs text-blue-500 transition-colors hover:bg-blue-500/10 dark:text-blue-400"
            >
              <Plus size={14} />
              Add Key
            </button>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">
              {error}
            </div>
          )}
          {actionError && (
            <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">
              {actionError}
            </div>
          )}

          {/* Action Buttons */}
          <div className="border-border mt-5 flex items-center gap-3 border-t pt-4">
            {!isConnected && credentials.length > 0 && (
              <button
                onClick={() => connect()}
                disabled={isConnecting}
                title="Open a live Kalshi session with your stored keys to load your account state and enable account actions."
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600"
              >
                {isConnecting && <Loader2 size={14} className="animate-spin" />}
                {isConnecting ? 'Connecting...' : 'Connect'}
              </button>
            )}
            {isConnected && (
              <button
                onClick={disconnect}
                title="Close the live Kalshi session. Your stored keys stay saved — reconnect anytime."
                className="bg-secondary text-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors"
              >
                Disconnect
              </button>
            )}
          </div>
        </section>

        {/* Polymarket Account Card — hidden for trader-role users (personal-
            key Polymarket disabled by the org). Admin/manager paths intact. */}
        {personalPolyHidden ? (
          <div
            data-testid="personal-poly-disabled-notice"
            className="bg-card border-border flex items-start gap-3 rounded-lg border px-4 py-3 text-xs text-muted-foreground"
          >
            <ShieldOff size={14} className="mt-0.5 shrink-0" />
            <div>
              <p className="text-foreground font-medium">Personal Polymarket trading not available</p>
              <p className="mt-0.5">
                Trader accounts use the org&apos;s shared Polymarket credential. Ask an
                admin or manager to configure org-level Polymarket keys if your
                workflow needs it.
              </p>
            </div>
          </div>
        ) : (
          <PolymarketAccountCard />
        )}
      </div>
      <ConfirmModal
        isOpen={!!hardDeleteTarget}
        onClose={() => setHardDeleteTarget(null)}
        onConfirm={handleHardDeleteKey}
        title="Permanently Delete Key"
        message={`Permanently delete "${hardDeleteTarget?.label}"? This removes encrypted key material and lifecycle history. Disable is safer unless this key should never be recoverable.`}
        confirmLabel="Delete Permanently"
        variant="danger"
      />
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────

function StatusBadge({
  status,
  balance,
  keyMode,
}: {
  status: string;
  balance: number | null;
  keyMode?: 'org' | 'user' | null;
}) {
  const dot =
    status === 'connected' ? 'bg-green-500' :
    status === 'connecting' ? 'bg-yellow-500 animate-pulse' :
    status === 'error' ? 'bg-red-500' :
    'bg-muted-foreground/40';

  const baseLabel =
    status === 'connected' ? 'Connected' :
    status === 'connecting' ? 'Connecting...' :
    status === 'error' ? 'Error' :
    'Disconnected';

  // Show the resolved key pool whenever we know it (connected or otherwise) so
  // a user can tell at a glance whether the relay is routing them through org
  // or personal keys — surfaced specifically because org-key fallback bugs
  // were invisible from this badge.
  const modeSuffix = keyMode ? ` (${keyMode === 'org' ? 'Org' : 'Personal'})` : '';
  const label = `${baseLabel}${modeSuffix}`;

  return (
    <div className="flex items-center gap-3 text-xs">
      {status === 'connected' && balance !== null && (
        <span className="text-muted-foreground" title={kalshiReportedBalanceTitle(keyMode)}>
          Balance: <span className="text-foreground font-medium">${(balance / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
        </span>
      )}
      <div className="flex items-center gap-1.5">
        <span className={`inline-block h-2 w-2 rounded-full ${dot}`} />
        <span className="text-muted-foreground">{label}</span>
      </div>
    </div>
  );
}

// ── Credential Row ──────────────────────────────────────────────

function CredentialRow({
  credential,
  acting,
  onRelease,
  onToggleDisabled,
  onHardDelete,
}: {
  credential: StoredCredential;
  acting: boolean;
  onRelease: () => void;
  onToggleDisabled: () => void;
  onHardDelete: () => void;
}) {
  const state = getCredentialState(credential);
  const disabled = state.kind === 'disabled';
  const canRelease =
    !disabled &&
    (!!credential.quarantinedUntil ||
      Number(credential.consecutiveAuthFailures ?? 0) > 0 ||
      !!credential.lastErrorAt);
  const truncated =
    credential.accessKey.length > 10
      ? `${credential.accessKey.slice(0, 6)}...${credential.accessKey.slice(-4)}`
      : credential.accessKey;

  return (
    <div className={`bg-muted/50 rounded-md px-3 py-2 ${disabled ? 'opacity-75' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-foreground text-xs font-medium">{credential.label}</span>
            <CredentialStateBadge credential={credential} />
            <span className="bg-secondary text-muted-foreground rounded px-1.5 py-0.5 text-[10px]">
              {credential.environment}
            </span>
          </div>
          <span className="text-muted-foreground block font-mono text-xs">{truncated}</span>
          <CredentialHealthLines credential={credential} />
        </div>
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
          {canRelease && (
            <button
              onClick={onRelease}
              disabled={acting}
              title="Clear this key's current error/quarantine state and reset its auth-failure count so it can be tried again."
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-blue-500/10 hover:text-blue-500 disabled:opacity-50"
            >
              <RefreshCw size={12} className={acting ? 'animate-spin' : ''} />
              Release
            </button>
          )}
          <button
            onClick={onToggleDisabled}
            disabled={acting}
            title={disabled ? 'Turn this key back on so it can connect and trade.' : 'Stop using this key without deleting it — it stays stored and can be re-enabled anytime.'}
            className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-muted hover:text-foreground disabled:opacity-50"
          >
            <Ban size={12} />
            {disabled ? 'Enable' : 'Disable'}
          </button>
          {disabled && (
            <button
              onClick={onHardDelete}
              disabled={acting}
              title="Permanently remove this stored key — you'd have to re-add it to use it again. Only available while the key is disabled."
              className="text-muted-foreground inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] hover:bg-red-500/10 hover:text-red-500 disabled:opacity-50"
            >
              <Trash2 size={12} />
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Add Key Form ────────────────────────────────────────────────

function AddKeyForm({
  onAdd,
  onCancel,
}: {
  onAdd: (key: {
    label: string;
    accessKey: string;
    privateKeyPem: string;
    environment: 'prod' | 'demo';
  }) => Promise<boolean>;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState('');
  const [accessKey, setAccessKey] = useState('');
  const [privateKeyPem, setPrivateKeyPem] = useState('');
  const [environment, setEnvironment] = useState<'prod' | 'demo'>('prod');
  const [showPem, setShowPem] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setLocalError(null);

    if (!accessKey.trim()) { setLocalError('Access key is required.'); return; }
    if (!privateKeyPem.trim()) { setLocalError('Private key PEM is required.'); return; }
    if (!privateKeyPem.includes('BEGIN PRIVATE KEY') && !privateKeyPem.includes('BEGIN RSA PRIVATE KEY')) {
      setLocalError('PEM must start with -----BEGIN PRIVATE KEY----- or -----BEGIN RSA PRIVATE KEY-----');
      return;
    }

    setSubmitting(true);
    try {
      const ok = await onAdd({
        label: label.trim() || `Key ${Date.now().toString(36)}`,
        accessKey: accessKey.trim(),
        privateKeyPem: privateKeyPem.trim(),
        environment,
      });
      if (ok) {
        setLabel('');
        setAccessKey('');
        setPrivateKeyPem('');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-muted/50 border-border rounded-md border p-3">
      <div className="space-y-3">
        {/* Environment */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Environment</label>
          <div className="flex gap-2">
            {(['prod', 'demo'] as const).map((env) => (
              <button key={env} onClick={() => setEnvironment(env)}
                className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  environment === env
                    ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}>
                {env === 'prod' ? 'Production' : 'Demo'}
              </button>
            ))}
          </div>
          <p className="text-muted-foreground mt-1.5 text-xs leading-snug">
            Production trades your real Kalshi account with real money. Demo is Kalshi's practice sandbox — pick it to test without risking funds.
          </p>
        </div>

        {/* Label */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Label (optional)</label>
          <input type="text" value={label}
            onChange={(e) => { setLabel(e.target.value); setLocalError(null); }}
            placeholder="e.g., Trading Key 1"
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 text-xs focus:border-blue-500 focus:outline-none" />
          <p className="text-muted-foreground mt-1.5 text-xs leading-snug">
            An optional nickname to tell your stored keys apart. Cosmetic only — it doesn't affect trading.
          </p>
        </div>

        {/* Access Key */}
        <div>
          <label className="text-muted-foreground mb-1 block text-xs">Access Key</label>
          <input type="password" value={accessKey}
            onChange={(e) => { setAccessKey(e.target.value); setLocalError(null); }}
            placeholder="Your Kalshi access key ID"
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none" />
          <p className="text-muted-foreground mt-1.5 text-xs leading-snug">
            The public ID half of your Kalshi API key pair; it identifies which key each request comes from.
          </p>
        </div>

        {/* Private Key PEM */}
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="text-muted-foreground text-xs">Private Key (PKCS#8 PEM)</label>
            <button onClick={() => setShowPem((v) => !v)} className="text-muted-foreground hover:text-foreground">
              {showPem ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
          <textarea value={privateKeyPem}
            onChange={(e) => { setPrivateKeyPem(e.target.value); setLocalError(null); }}
            placeholder="-----BEGIN PRIVATE KEY-----"
            rows={4}
            className="bg-card border-border text-foreground placeholder-muted-foreground w-full resize-y rounded-md border px-3 py-1.5 font-mono text-xs focus:border-blue-500 focus:outline-none"
            style={!showPem && privateKeyPem ? { WebkitTextSecurity: 'disc' } as React.CSSProperties : undefined} />
          <p className="text-muted-foreground mt-1.5 text-xs leading-snug">
            The secret half of your API key pair — it signs your orders. Paste the entire PEM block, including the BEGIN/END lines.
          </p>
        </div>

        {localError && <div className="text-xs text-red-500 dark:text-red-400">{localError}</div>}

        <div className="flex items-center gap-2">
          <button onClick={() => void handleSubmit()} disabled={submitting}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50">
            {submitting ? 'Saving...' : 'Add Key'}
          </button>
          <button onClick={onCancel} disabled={submitting}
            className="text-muted-foreground hover:text-foreground rounded-md px-3 py-1.5 text-xs transition-colors disabled:opacity-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Polymarket Account Card ────────────────────────────────────

const POLY_SIG_TYPE_DESCRIPTIONS: Record<0 | 1 | 2 | 3, string> = {
  0: 'Self-custodied EOA (MetaMask, programmatic). Signer and funder are the same address — no separate funder needed.',
  1: 'Existing Magic Link email-login users. The key signs orders; a Polymarket proxy contract holds your collateral.',
  2: 'Existing browser-wallet users with a Polymarket Safe. The key signs orders; a Gnosis Safe holds your collateral.',
  3: 'New polymarket.com onboardees (POLY_1271 / Deposit Wallet). The key signs ERC-7739 wrapped orders; a CREATE2-derived deposit wallet holds your collateral. Paste the wallet address shown on your Polymarket profile as the funder.',
};

function PolymarketAccountCard() {
  const polyStatus = usePolyAccountStore((s) => s.status);
  const polyError = usePolyAccountStore((s) => s.error);
  const polyConfig = usePolyAccountStore((s) => s.config);
  const polyFunderBalance = usePolyAccountStore((s) => s.funderBalance);
  const polyConnect = usePolyAccountStore((s) => s.connect);
  const polyDisconnect = usePolyAccountStore((s) => s.disconnect);
  const polyRemoveCredentials = usePolyAccountStore((s) => s.removeCredentials);

  const [privateKey, setPrivateKey] = useState('');
  // Default to Gnosis Safe, the common existing browser-wallet setup this
  // settings card supports.
  const [signatureType, setSignatureType] = useState<0 | 1 | 2 | 3>(2);
  const [funder, setFunder] = useState('');
  const [showKey, setShowKey] = useState(false);

  const isConnected = polyStatus === 'connected';
  const isConnecting = polyStatus === 'connecting';

  const pkTrim = privateKey.trim();
  const funderTrim = funder.trim();
  const pkFormatError = pkTrim && !isValidEvmPrivateKey(pkTrim)
    ? 'Must be a valid 0x + 64-hex EVM private key.'
    : null;
  const showFunderMissing = signatureType !== 0 && !!pkTrim && !funderTrim;
  const funderFormatError =
    signatureType !== 0 && funderTrim && !isValidEvmAddress(funderTrim)
      ? 'Must be a valid 0x address (40 hex chars).'
      : null;
  const funderError = showFunderMissing
    ? 'Funder address is required for Magic, Gnosis, and Deposit Wallet.'
    : funderFormatError;
  const funderMissing = signatureType !== 0 && !funderTrim;
  const canSubmit =
    !!pkTrim && !pkFormatError && !funderError && !funderMissing && !isConnecting;

  const handleConnect = useCallback(async () => {
    if (!privateKey.trim()) return;
    await polyConnect({
      privateKey: privateKey.trim(),
      signatureType,
      funder: funder.trim() || undefined,
    });
  }, [privateKey, signatureType, funder, polyConnect]);

  const handleDisconnect = useCallback(() => {
    polyDisconnect();
    setPrivateKey('');
    setFunder('');
  }, [polyDisconnect]);

  const handleRemoveCredentials = useCallback(() => {
    polyRemoveCredentials();
    setPrivateKey('');
    setFunder('');
  }, [polyRemoveCredentials]);

  return (
    <div className="bg-card border-border rounded-lg border p-5">
      <div className="mb-5 flex items-center justify-between">
        <h2 className="text-base font-medium">Polymarket Account</h2>
        <StatusBadge status={polyStatus} balance={null} />
      </div>

      {!isConnected && (
        <>
          <div className="mb-4">
            <div className="mb-1 flex items-center justify-between">
              <label className="text-muted-foreground text-xs font-medium">Private Key</label>
              <button onClick={() => setShowKey((v) => !v)} className="text-muted-foreground hover:text-foreground">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <input type={showKey ? 'text' : 'password'} value={privateKey}
              onChange={(e) => setPrivateKey(e.target.value)} placeholder="0x..."
              aria-invalid={!!pkFormatError}
              className={`bg-card text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:outline-none ${
                pkFormatError ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-blue-500'
              }`} />
            {pkFormatError ? (
              <div className="mt-1 text-xs text-red-500 dark:text-red-400">{pkFormatError}</div>
            ) : (
              <p className="text-muted-foreground mt-1 text-xs leading-snug">
                The signing key that authorizes your Polymarket orders. Choose a signature type below.
              </p>
            )}
          </div>

          <div className="mb-4">
            <label className="text-muted-foreground mb-1.5 block text-xs font-medium">Signature Type</label>
            <div className="flex gap-2">
              {([0, 1, 2, 3] as const).map((t) => {
                const lbl =
                  t === 0 ? 'EOA' : t === 1 ? 'Magic' : t === 2 ? 'Gnosis' : 'Deposit';
                return (
                  <button key={t} onClick={() => setSignatureType(t)}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                      signatureType === t
                        ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
                        : 'bg-secondary text-muted-foreground hover:text-foreground'
                    }`}>
                    {lbl}
                  </button>
                );
              })}
            </div>
            <p className="text-muted-foreground mt-2 text-xs leading-snug">
              {POLY_SIG_TYPE_DESCRIPTIONS[signatureType]}
            </p>
          </div>

          {signatureType !== 0 && (
            <div className="mb-4">
              <label className="text-muted-foreground mb-1 block text-xs font-medium">
                Funder Address <span className="text-red-500">*</span>
              </label>
              <input type="text" value={funder} onChange={(e) => setFunder(e.target.value)}
                placeholder="0x..."
                aria-invalid={!!funderError}
                className={`bg-card text-foreground placeholder-muted-foreground w-full rounded-md border px-3 py-1.5 font-mono text-xs focus:outline-none ${
                  funderError ? 'border-red-500 focus:border-red-500' : 'border-border focus:border-blue-500'
                }`} />
              {funderError ? (
                <div className="mt-1 text-xs text-red-500 dark:text-red-400">{funderError}</div>
              ) : (
                <p className="text-muted-foreground mt-1 text-xs leading-snug">
                  The wallet address shown on your Polymarket profile — the proxy/Safe that holds your collateral, not the address derived from your signer key.
                </p>
              )}
            </div>
          )}
        </>
      )}

      {isConnected && polyConfig && (
        <div className="mb-4 space-y-3">
          <div>
            <label className="text-muted-foreground mb-1 block text-xs font-medium">Signer Address</label>
            <div className="bg-muted/50 text-foreground break-all rounded-md px-3 py-2 font-mono text-xs">
              {polyConfig.signerAddress ?? '—'}
            </div>
          </div>
          {polyConfig.signatureType !== 0 && polyConfig.funder && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs font-medium">Funder Address</label>
              <div className="bg-muted/50 text-foreground break-all rounded-md px-3 py-2 font-mono text-xs">
                {polyConfig.funder}
              </div>
            </div>
          )}
          {polyFunderBalance && (
            <div>
              <label className="text-muted-foreground mb-1 block text-xs font-medium">Collateral USDC</label>
              <div className="bg-muted/50 text-foreground rounded-md px-3 py-2 font-mono text-xs">
                ${polyFunderBalance.collateralUsd.toFixed(2)}
                <span className="text-muted-foreground ml-2">
                  (allowance ${polyFunderBalance.collateralAllowanceUsd.toFixed(2)})
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {polyError && (
        <div className="mt-4 rounded-md bg-red-500/10 px-3 py-2 text-xs text-red-500 dark:text-red-400">{polyError}</div>
      )}

      <div className="border-border mt-5 flex items-center gap-3 border-t pt-4">
        {!isConnected ? (
          <button onClick={handleConnect} disabled={!canSubmit}
            className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600">
            {isConnecting && <Loader2 size={14} className="animate-spin" />}
            {isConnecting ? 'Connecting...' : 'Connect'}
          </button>
        ) : (
          <>
            <button onClick={handleDisconnect}
              className="bg-secondary text-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors">
              Disconnect
            </button>
            <button onClick={handleRemoveCredentials}
              className="rounded-md px-3 py-2 text-xs text-red-500 transition-colors hover:bg-red-500/10 dark:text-red-400">
              Remove Credentials
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── User Preferences (local-only, persisted to localStorage) ─────

const MARKET_TYPE_CHOICES: { id: DashboardMarketType; label: string }[] = [
  { id: 'moneyline', label: 'Moneyline' },
  { id: 'spread-total', label: 'Spread/Total' },
  { id: 'series', label: 'Futures' },
];

const PRICE_MODE_CHOICES: { id: PriceMode; label: string; hint: string }[] = [
  { id: 'maker', label: 'Maker', hint: 'Best bid — what someone will pay you' },
  { id: 'taker', label: 'Taker', hint: 'Best ask — what you pay to cross now' },
];

const EXPIRY_MODE_CHOICES: { id: DefaultExpiryMode; label: string; hint: string }[] = [
  // Says "fixed when you place", not "at scheduled event start". The venue
  // captures the start once and never revisits it, so a postponement leaves the
  // expiry behind — the #510 signature, and the belief that made a vanished
  // order inexplicable to the trader who placed it (#517).
  {
    id: 'gts',
    label: 'Good to Start',
    hint: 'Expires at the event start as it stood when you placed. The venue fixes that time — a later reschedule does not move it, so a postponed game can expire the order before it starts.',
  },
  { id: 'gtc', label: 'Good to Close', hint: 'Rests until you cancel or the market settles' },
  { id: 'custom', label: 'Custom', hint: 'Open the ticket on the custom datetime picker' },
];

// Dashboard sort default — `unsorted` is a UI-only sentinel that maps to
// `key: null` in displaySettings; selecting it hides the direction picker
// since asc/desc has no meaning with no applied sort. Keep this list in sync
// with DashboardSortKey + the SortableTh keys in GameTable.tsx.
type DashboardSortKeyChoice = 'unsorted' | DashboardSortKey;
const DASHBOARD_SORT_KEY_CHOICES: { id: DashboardSortKeyChoice; label: string }[] = [
  { id: 'unsorted', label: 'Unsorted' },
  { id: 'date', label: 'Date' },
  { id: 'team', label: 'Team' },
  { id: 'queue', label: 'Queue' },
  { id: 'velocity', label: 'Velocity' },
];
const DASHBOARD_SORT_DIR_CHOICES: { id: DashboardSortDir; label: string }[] = [
  { id: 'asc', label: 'Asc' },
  { id: 'desc', label: 'Desc' },
];

// Only STABLE keys are offered as a saved default. 'queue'/'velocity' sort on live
// data (the live page re-settles them every 2s), so readPositionsOrderSort
// (displaySettings.ts) deliberately coerces a saved default of either back to
// 'default' on load — offering them here
// would let a trader "save" a choice the loader silently drops. Both remain fully
// selectable live on the Positions page (per-session, via positionsOrderSortSession).
const POSITIONS_ORDER_SORT_KEY_CHOICES: { id: PositionsOrderSortKey; label: string }[] = [
  { id: 'default', label: 'Default' },
  { id: 'size', label: 'Size' },
];
const POSITIONS_ORDER_SORT_DIR_CHOICES: { id: PositionsOrderSortDir; label: string }[] = [
  { id: 'asc', label: 'Asc' },
  { id: 'desc', label: 'Desc' },
];
const BOT_BOARD_VIEW_CHOICES: { id: BotBoardViewMode; label: string }[] = [
  { id: 'compact', label: 'Compact' },
  { id: 'card', label: 'Card' },
  { id: 'table', label: 'Table' },
];
const BOT_BOARD_SORT_CHOICES: { id: BotBoardSortKey; label: string }[] = [
  { id: 'front', label: 'Closest front' },
  { id: 'eta', label: 'Soonest fill' },
  { id: 'net', label: 'Inventory drift' },
  { id: 'errors', label: 'Errors' },
];

/** Exported for the #610 release-boundary tests, which drive the real panel
 *  rather than a copy of its capability gating. Rendered only from `Settings`. */
export function PreferencesPanel() {
  const positionViewMode = useDisplaySettings((s) => s.positionViewMode);
  const orderViewMode = useDisplaySettings((s) => s.orderViewMode);
  const tradeViewMode = useDisplaySettings((s) => s.tradeViewMode);
  const spreadTotalViewMode = useDisplaySettings((s) => s.spreadTotalViewMode);
  const gameLinesViewMode = useDisplaySettings((s) => s.gameLinesViewMode);
  const moneylineViewMode = useDisplaySettings((s) => s.moneylineViewMode);
  const botFleetViewMode = useDisplaySettings((s) => s.botFleetViewMode);
  const myBotsViewMode = useDisplaySettings((s) => s.myBotsViewMode);
  const orgBotsViewMode = useDisplaySettings((s) => s.orgBotsViewMode);
  const botFleetSort = useDisplaySettings((s) => s.botFleetSort);
  const myBotsSort = useDisplaySettings((s) => s.myBotsSort);
  const orgBotsSort = useDisplaySettings((s) => s.orgBotsSort);
  const defaultSport = useDisplaySettings((s) => s.defaultSport);
  const defaultMarketType = useDisplaySettings((s) => s.defaultMarketType);
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  const bookDepthLevels = useDisplaySettings((s) => s.bookDepthLevels);
  const setBookDepthLevels = useDisplaySettings((s) => s.setBookDepthLevels);
  const defaultExpiryMode = useDisplaySettings((s) => s.defaultExpiryMode);
  const defaultPolyExpiryMode = useDisplaySettings((s) => s.defaultPolyExpiryMode);
  const defaultContracts = useDisplaySettings((s) => s.defaultContracts);
  const defaultDashboardSort = useDisplaySettings((s) => s.defaultDashboardSort);
  const defaultPositionsOrderSort = useDisplaySettings((s) => s.defaultPositionsOrderSort);
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  const setPositionViewMode = useDisplaySettings((s) => s.setPositionViewMode);
  const setOrderViewMode = useDisplaySettings((s) => s.setOrderViewMode);
  const setTradeViewMode = useDisplaySettings((s) => s.setTradeViewMode);
  const setSpreadTotalViewMode = useDisplaySettings((s) => s.setSpreadTotalViewMode);
  const setGameLinesViewMode = useDisplaySettings((s) => s.setGameLinesViewMode);
  const setMoneylineViewMode = useDisplaySettings((s) => s.setMoneylineViewMode);
  const setBotFleetViewMode = useDisplaySettings((s) => s.setBotFleetViewMode);
  const setMyBotsViewMode = useDisplaySettings((s) => s.setMyBotsViewMode);
  const setOrgBotsViewMode = useDisplaySettings((s) => s.setOrgBotsViewMode);
  const setBotFleetSort = useDisplaySettings((s) => s.setBotFleetSort);
  const setMyBotsSort = useDisplaySettings((s) => s.setMyBotsSort);
  const setOrgBotsSort = useDisplaySettings((s) => s.setOrgBotsSort);
  const setDefaultSport = useDisplaySettings((s) => s.setDefaultSport);
  const setDefaultMarketType = useDisplaySettings((s) => s.setDefaultMarketType);
  const setDefaultPriceMode = useDisplaySettings((s) => s.setDefaultPriceMode);
  const setDefaultExpiryMode = useDisplaySettings((s) => s.setDefaultExpiryMode);
  const setDefaultPolyExpiryMode = useDisplaySettings((s) => s.setDefaultPolyExpiryMode);
  const setDefaultContracts = useDisplaySettings((s) => s.setDefaultContracts);
  const setDefaultDashboardSort = useDisplaySettings((s) => s.setDefaultDashboardSort);
  const setDefaultPositionsOrderSort = useDisplaySettings((s) => s.setDefaultPositionsOrderSort);
  const setConfirmOrderCancel = useDisplaySettings((s) => s.setConfirmOrderCancel);
  const pinnedOrderTicket = useDisplaySettings((s) => s.pinnedOrderTicket);
  const setPinnedOrderTicket = useDisplaySettings((s) => s.setPinnedOrderTicket);
  // The one canonical light/dark value, shared with the header rail toggle —
  // this row is a second surface on it, not a second preference.
  const { theme, setTheme } = useTheme();
  // Oscar overtake prefs — rendered only for traders with the server-side flag.
  const oscarAccess = useOscarAccess();
  const defaultBlockerContracts = useDisplaySettings((s) => s.defaultBlockerContracts);
  const setDefaultBlockerContracts = useDisplaySettings((s) => s.setDefaultBlockerContracts);
  const oscarBlockerDefaultOn = useDisplaySettings((s) => s.oscarBlockerDefaultOn);
  const toggleOscarBlockerDefaultOn = useDisplaySettings((s) => s.toggleOscarBlockerDefaultOn);
  const showOpOdds = useDisplaySettings((s) => s.showOpOdds);
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const setShowUnbtdOdds = useDisplaySettings((s) => s.setShowUnbtdOdds);
  const showOoOdds = useDisplaySettings((s) => s.showOoOdds);
  const setShowOoOdds = useDisplaySettings((s) => s.setShowOoOdds);
  const setShowOpOdds = useDisplaySettings((s) => s.setShowOpOdds);
  const restingOrderDisplay = useDisplaySettings((s) => s.restingOrderDisplay);
  const setRestingOrderDisplay = useDisplaySettings((s) => s.setRestingOrderDisplay);
  const slateRange = useDisplaySettings((s) => s.slateRange);
  const setSlateRange = useDisplaySettings((s) => s.setSlateRange);
  // D-020 Live Trading V2 prefs — rendered only with the server-side flag.
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const lt2PullAllEnabled = useDisplaySettings((s) => s.lt2PullAllEnabled);
  const setLt2HotkeysEnabled = useDisplaySettings((s) => s.setLt2HotkeysEnabled);
  const setLt2PullAllEnabled = useDisplaySettings((s) => s.setLt2PullAllEnabled);
  const spreadTotalNearestLines = useDisplaySettings((s) => s.spreadTotalNearestLines);
  const setSpreadTotalNearestLines = useDisplaySettings((s) => s.setSpreadTotalNearestLines);

  return (
    <>
      <PrefGroup id="prefs-dashboard" title="Dashboard and ladders">
        <PrefSection
          label="Appearance"
          hint="Light or dark for the whole app. The same choice as the sun/moon icon in the header rail — both read and write one stored value, so changing either updates the other at once. Until you pick one, the app follows your operating system's setting; picking here or in the header stops that."
        >
          <ChoiceRow
            options={[
              { id: 'light', label: 'Light' },
              { id: 'dark', label: 'Dark' },
            ]}
            value={theme}
            // Writes unless the click is genuinely inert: same theme AND no
            // choice stored yet. In that one state the app is following the OS
            // and this row is showing what that resolved to, so recording a
            // pick would pin the theme and end the following, with no control
            // anywhere to undo it (#690 forbids a third stored value) — while
            // changing nothing the trader can see. Pinning the current theme
            // is still reachable: pick the other chip, then back.
            //
            // The condition asks storage, not this tab's rendered theme. They
            // disagree after another tab writes — theme has no cross-tab sync —
            // and a guard on the rendered value alone would swallow a real pick
            // there, leaving storage on the other tab's answer.
            onChange={(id) => {
              if (id !== theme || hasStoredTheme()) setTheme(id as Theme);
            }}
          />
        </PrefSection>

        <PrefSection
          label="Default sport"
          hint="Which sport tab the Value Dashboard lands on."
        >
          <ChoiceRow
            options={SPORT_TABS.map((t) => ({ id: t.id, label: t.label }))}
            value={defaultSport}
            onChange={(id) => setDefaultSport(id as Sport)}
          />
        </PrefSection>

        <PrefSection
          label="Default market type"
          hint="Which market-type tab opens first."
        >
          <ChoiceRow
            options={MARKET_TYPE_CHOICES}
            value={defaultMarketType}
            onChange={(id) => setDefaultMarketType(id as DashboardMarketType)}
          />
        </PrefSection>

        <PrefSection
          label="Default price mode"
          hint={
            PRICE_MODE_CHOICES.find((c) => c.id === defaultPriceMode)?.hint ?? ''
          }
        >
          <ChoiceRow
            options={PRICE_MODE_CHOICES}
            value={defaultPriceMode}
            onChange={(id) => setDefaultPriceMode(id as PriceMode)}
          />
        </PrefSection>

        <PrefSection
          label="Default dashboard sort"
          hint="Initial sort applied when the Value Dashboard mounts. Header clicks override for the session and don't change this default — set the sort you want pinned across reloads here. 'Unsorted' leaves games in upstream order (the prior behavior)."
        >
          <ChoiceRow
            options={DASHBOARD_SORT_KEY_CHOICES}
            value={defaultDashboardSort.key ?? 'unsorted'}
            onChange={(id) => {
              const key = id === 'unsorted' ? null : (id as DashboardSortKey);
              setDefaultDashboardSort({ key, dir: defaultDashboardSort.dir });
            }}
          />
          {defaultDashboardSort.key !== null && (
            <div className="mt-2">
              <ChoiceRow
                options={DASHBOARD_SORT_DIR_CHOICES}
                value={defaultDashboardSort.dir}
                onChange={(id) =>
                  setDefaultDashboardSort({
                    key: defaultDashboardSort.key,
                    dir: id as DashboardSortDir,
                  })
                }
              />
            </div>
          )}
        </PrefSection>

        <PrefSection
          label="Football slate range"
          hint="How wide the Value Dashboard's date view is on the NFL and CFB game boards — one day, or the Monday–Sunday week containing the selected date. Week shows every football game in that range across the Moneyline and Spread/Total boards, and the date arrows then step a whole week. Day is the default and leaves those boards exactly as they are. Every other sport, and Futures, keep the daily view whichever is set. The dashboard toolbar's Day/Week chips flip the same setting."
        >
          <ChoiceRow
            options={[
              { id: 'day', label: 'Day' },
              { id: 'week', label: 'Week' },
            ]}
            value={slateRange}
            onChange={(id) => setSlateRange(id as SlateRange)}
          />
        </PrefSection>

        <PrefSection
          label="Unabated comparison odds"
          hint="The UNBTD consensus column. Hidden by default (Odds filter, 2026-08-23): OddsPapi is the going-forward comparison feed and Unabated is being phased out. The dashboard toolbar's Odds chips flip the same setting."
        >
          <ChoiceRow
            options={[
              { id: 'show', label: 'Show' },
              { id: 'hide', label: 'Hide' },
            ]}
            value={showUnbtdOdds ? 'show' : 'hide'}
            onChange={(id) => setShowUnbtdOdds(id === 'show')}
          />
        </PrefSection>

        <PrefSection
          label="OO comparison odds (OpticOdds trial)"
          hint="The OO/PNCL column — a second, independent comparison vendor served beside OddsPapi. Hidden by default; even when shown it only appears for sports the trial serves. OO and OP consensus deliberately use different book sets — the comparable quantity is the Pinnacle line."
        >
          <ChoiceRow
            options={[
              { id: 'show', label: 'Show' },
              { id: 'hide', label: 'Hide' },
            ]}
            value={showOoOdds ? 'show' : 'hide'}
            onChange={(id) => setShowOoOdds(id === 'show')}
          />
        </PrefSection>

        <PrefSection
          label="OP comparison odds"
          hint="OddsPapi de-vigged comparison values: the OP/PNCL column beside the dashboard Odds column and the O row under the trade panel's U row — consensus fair % and pinnacle's own de-vig side by side, computed by hydra, display-only. Even when shown, the surfaces auto-hide while the feed carries no OP data."
        >
          <ChoiceRow
            options={[
              { id: 'show', label: 'Show' },
              { id: 'hide', label: 'Hide' },
            ]}
            value={showOpOdds ? 'show' : 'hide'}
            onChange={(id) => setShowOpOdds(id === 'show')}
          />
        </PrefSection>

        <PrefSection
          label="Resting orders"
          // Templated from INLINE_CHIP_LIMIT for the same reason as the status
          // bar mirror: the number is a knob, and copy that spelled it out
          // would start lying the moment it moved.
          hint={
            `How a ladder rung shows YOUR resting orders at that price — the dashboard moneyline ladder, the spread/total ladder and the LT2 two-sided ladder. ` +
            `The first option shows the ${INLINE_CHIP_LIMIT} nearest the front of the queue with the rest behind a +N you can open; All shows every one in a contained region that wraps and scrolls, headed by a count. ` +
            `Both list the same orders in the same order, and every order stays individually cancellable either way. The compact ${INLINE_CHIP_LIMIT} is the default. Mirrored by the 'Orders' button in the status bar.`
          }
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: `${INLINE_CHIP_LIMIT}, rest behind +N` },
              { id: 'expanded', label: 'All' },
            ]}
            value={restingOrderDisplay}
            onChange={(id) => setRestingOrderDisplay(id as RestingOrderDisplay)}
          />
        </PrefSection>

        {lt2Access && (
          <PrefSection
            label="Live Trading V2: multi-line ladders"
            hint="Expanding a spread/total row shows the nearest three lines' two-sided books side by side instead of just the selected line."
          >
            <ChoiceRow
              options={[
                { id: 'off', label: 'Selected line only' },
                { id: 'on', label: 'Nearest three' },
              ]}
              value={spreadTotalNearestLines ? 'on' : 'off'}
              onChange={(id) => setSpreadTotalNearestLines(id === 'on')}
            />
          </PrefSection>
        )}
      </PrefGroup>

      <PrefGroup id="prefs-gamedetail" title="Game Detail">
        <PrefSection
          label="Moneyline view"
          hint="Default density for the game-detail Moneyline section."
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: 'Compact' },
              { id: 'detailed', label: 'Detailed' },
            ]}
            value={moneylineViewMode}
            onChange={(id) => setMoneylineViewMode(id as MarketSectionViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Spread/Total view"
          hint="Default density for the game-detail Spread/Total section."
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: 'Compact' },
              { id: 'detailed', label: 'Detailed' },
            ]}
            value={spreadTotalViewMode}
            onChange={(id) => setSpreadTotalViewMode(id as MarketSectionViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Game Lines view"
          hint="Default density for the game-detail Game Lines section — all tabs."
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: 'Compact' },
              { id: 'detailed', label: 'Detailed' },
            ]}
            value={gameLinesViewMode}
            onChange={(id) => setGameLinesViewMode(id as MarketSectionViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Order book depth"
          hint="Price levels shown per side in the trade panel's Kalshi/Polymarket books. 8 is the classic full fan; 3 is the tight current-price view."
        >
          <ChoiceRow
            options={[
              { id: '3', label: '3 levels' },
              { id: '5', label: '5 levels' },
              { id: '8', label: '8 levels' },
            ]}
            value={String(bookDepthLevels)}
            onChange={(id) => setBookDepthLevels(Number(id))}
          />
        </PrefSection>

        <PrefSection
          label="Trade panel scroll"
          hint="How the side trade panel scrolls when it's taller than the screen. Pinned ticket keeps the order ticket fixed and scrolls only the order book (classic — BUY / Place Order always in view). Scroll whole panel scrolls everything together, so nothing is trapped on a short laptop screen."
        >
          <ChoiceRow
            options={[
              { id: 'pinned', label: 'Pinned ticket' },
              { id: 'whole', label: 'Scroll whole panel' },
            ]}
            value={pinnedOrderTicket ? 'pinned' : 'whole'}
            onChange={(id) => setPinnedOrderTicket(id === 'pinned')}
          />
        </PrefSection>
      </PrefGroup>

      <PrefGroup id="prefs-positions" title="Positions and orders">
        <PrefSection
          label="Positions view"
          hint="Default layout for the Open Positions section."
        >
          <ChoiceRow
            options={[
              { id: 'compact', label: 'Compact' },
              { id: 'table', label: 'Table' },
              { id: 'card', label: 'Card' },
              { id: 'mini', label: 'Mini' },
            ]}
            value={positionViewMode}
            onChange={(id) => setPositionViewMode(id as PositionViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Open orders view"
          hint="Default layout for the Open Orders section."
        >
          <ChoiceRow
            options={[
              { id: 'card', label: 'Card' },
              { id: 'table', label: 'Table' },
            ]}
            value={orderViewMode}
            onChange={(id) => setOrderViewMode(id as SurfaceViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Trades view"
          hint="Default layout for My Trades."
        >
          <ChoiceRow
            options={[
              { id: 'card', label: 'Card' },
              { id: 'table', label: 'Table' },
            ]}
            value={tradeViewMode}
            onChange={(id) => setTradeViewMode(id as SurfaceViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Default Open Orders sort"
          hint="Initial sort for Open Orders on the Positions page. Queue and Velocity stay selectable live on the page but can't be saved as a default — they sort on live data (the page re-settles them every 2s)."
        >
          <ChoiceRow
            options={POSITIONS_ORDER_SORT_KEY_CHOICES}
            value={defaultPositionsOrderSort.key}
            onChange={(id) =>
              setDefaultPositionsOrderSort({
                key: id as PositionsOrderSortKey,
                dir: defaultPositionsOrderSort.dir,
              })
            }
          />
          {defaultPositionsOrderSort.key !== 'default' && (
            <div className="mt-2">
              <ChoiceRow
                options={POSITIONS_ORDER_SORT_DIR_CHOICES}
                value={defaultPositionsOrderSort.dir}
                onChange={(id) =>
                  setDefaultPositionsOrderSort({
                    key: defaultPositionsOrderSort.key,
                    dir: id as PositionsOrderSortDir,
                  })
                }
              />
            </div>
          )}
        </PrefSection>
      </PrefGroup>

      <PrefGroup id="prefs-trading" title="Trading defaults">
        <PrefSection
          label="Default contracts"
          hint="Auto-fills the Qty input the first time you pick a price (orderbook click, pill click, etc.). Doesn't pre-populate on mount and never overwrites a value you've already typed. Also accessible via the '+ N' button next to the Qty label."
        >
          <input
            type="number"
            min={1}
            step={1}
            value={defaultContracts}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (Number.isFinite(n) && n >= 1) setDefaultContracts(n);
            }}
            className="border-border bg-input text-foreground focus:border-primary w-24 rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
          />
        </PrefSection>

        <PrefSection
          label="Quick sizes"
          hint="Up to three preset sizes shown as one-tap chips under the Qty field in the order ticket (both venues, Overtake included). A tap replaces the Qty. Leave a slot empty to hide its chip."
        >
          <QuickSizeSlots />
        </PrefSection>

        <PrefSection
          label="Default expiry"
          hint={
            EXPIRY_MODE_CHOICES.find((c) => c.id === defaultExpiryMode)?.hint ?? ''
          }
        >
          <ChoiceRow
            options={EXPIRY_MODE_CHOICES}
            value={defaultExpiryMode}
            onChange={(id) => setDefaultExpiryMode(id as DefaultExpiryMode)}
          />
        </PrefSection>

        <PrefSection
          label="Polymarket default expiry"
          // Both figures are the venue's own, from its GTD documentation, and
          // both were wrong here before #557: the CLOB expires a GTD about a
          // minute BEFORE the timestamp it was given (a security threshold),
          // and it rejects any expiry under three minutes out — not the
          // "~60-90s" this claimed. The expiry itself is fixed at placement
          // and never re-read, which is what the order surfaces warn about.
          //
          // It says the venue REJECTS, not that GTS is unavailable: the ticket
          // does not pre-check the 3-minute floor (`gtsAvailable` only requires
          // a future start), so close to the start the order is submitted and
          // fails at the venue. Copy that promised a greyed-out option would
          // just relabel that surprise.
          hint="Polymarket tickets only. GTS sends the event start as a fixed venue expiry (a GTD order), so it auto-cancels about a minute before the scheduled start and does not move if the game is rescheduled; the venue rejects any expiry under 3 minutes away, so GTS placed close to the start will fail."
        >
          <ChoiceRow
            options={EXPIRY_MODE_CHOICES}
            value={defaultPolyExpiryMode}
            onChange={(id) => setDefaultPolyExpiryMode(id as DefaultExpiryMode)}
          />
        </PrefSection>

        <PrefSection
          label="Cancel guard"
          hint="Controls direct order-cancel buttons. Require confirm keeps the extra safety click; one click cancels immediately."
        >
          <ChoiceRow
            options={[
              { id: 'confirm', label: 'Require confirm' },
              { id: 'instant', label: 'One click' },
            ]}
            value={confirmOrderCancel ? 'confirm' : 'instant'}
            onChange={(id) => setConfirmOrderCancel(id === 'confirm')}
          />
        </PrefSection>

        {oscarAccess && (
          <>
            <PrefSection
              label="Overtake: default blocker contracts"
              hint="Seeds the blocker Qty when you enter Overtake mode or arm a release (blank-Qty only — never overwrites a typed size). Leave empty to follow Default contracts."
            >
              <input
                type="number"
                min={1}
                step={1}
                value={defaultBlockerContracts ?? ''}
                placeholder={String(defaultContracts)}
                onChange={(e) => {
                  if (e.target.value === '') {
                    setDefaultBlockerContracts(null);
                    return;
                  }
                  const n = parseInt(e.target.value, 10);
                  if (Number.isFinite(n) && n >= 1) setDefaultBlockerContracts(n);
                }}
                className="border-border bg-input text-foreground placeholder-muted-foreground focus:border-primary w-24 rounded-md border px-2.5 py-1.5 font-mono text-xs focus:outline-none"
              />
            </PrefSection>

            <PrefSection
              label="Overtake: blocker default"
              hint="Whether the attach-blocker toggle starts ON when you enter Overtake mode. The blocker is a post-only opposite-side bid at 99 − take price."
            >
              <ChoiceRow
                options={[
                  { id: 'on', label: 'Blocker on' },
                  { id: 'off', label: 'Blocker off' },
                ]}
                value={oscarBlockerDefaultOn ? 'on' : 'off'}
                onChange={(id) => {
                  if ((id === 'on') !== oscarBlockerDefaultOn) toggleOscarBlockerDefaultOn();
                }}
              />
            </PrefSection>
          </>
        )}

        {lt2Access && (
          <PrefSection
            label="Live Trading V2: A/D hotkeys"
            hint="A posts the top/left side, D the bottom/right, on the FOCUSED derivative card only, at the visible one-click size, through the same stale/taker guard as a click. Requires arming 1-Click; Esc clears focus. Off keeps the hotkey layer fully dark."
          >
            <ChoiceRow
              options={[
                { id: 'off', label: 'Off' },
                { id: 'on', label: 'On' },
              ]}
              value={lt2HotkeysEnabled ? 'on' : 'off'}
              onChange={(id) => setLt2HotkeysEnabled(id === 'on')}
            />
          </PrefSection>
        )}

        {lt2Access && (
          <PrefSection
            label="Live Trading V3: Spacebar Pull All"
            hint="Spacebar cancels every resting Kalshi order on the FOCUSED game — including orders at off-screen levels — from authoritative order state, while 1-Click is armed and a card is aimed. Resting orders only; unrelated games and Polymarket orders are never touched. Off keeps Spacebar an ordinary browser key."
          >
            <ChoiceRow
              options={[
                { id: 'off', label: 'Off' },
                { id: 'on', label: 'On' },
              ]}
              value={lt2PullAllEnabled ? 'on' : 'off'}
              onChange={(id) => setLt2PullAllEnabled(id === 'on')}
            />
          </PrefSection>
        )}
      </PrefGroup>

      <PrefGroup id="prefs-bots" title="Bots and automation">
        <PrefSection
          label="Bot page view"
          hint="Default layout for the standalone bot fleet pages."
        >
          <ChoiceRow
            options={BOT_BOARD_VIEW_CHOICES}
            value={botFleetViewMode}
            onChange={(id) => setBotFleetViewMode(id as BotBoardViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="My Bots view"
          hint="Default layout for the Position Tracker's claimed bot section."
        >
          <ChoiceRow
            options={BOT_BOARD_VIEW_CHOICES}
            value={myBotsViewMode}
            onChange={(id) => setMyBotsViewMode(id as BotBoardViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Org bots view"
          hint="Default layout for the Position Tracker's org bot section."
        >
          <ChoiceRow
            options={BOT_BOARD_VIEW_CHOICES}
            value={orgBotsViewMode}
            onChange={(id) => setOrgBotsViewMode(id as BotBoardViewMode)}
          />
        </PrefSection>

        <PrefSection
          label="Bot page sort"
          hint="Default sort for the standalone bot fleet pages."
        >
          <ChoiceRow
            options={BOT_BOARD_SORT_CHOICES}
            value={botFleetSort}
            onChange={(id) => setBotFleetSort(id as BotBoardSortKey)}
          />
        </PrefSection>

        <PrefSection
          label="My Bots sort"
          hint="Default sort for the Position Tracker's claimed bot section."
        >
          <ChoiceRow
            options={BOT_BOARD_SORT_CHOICES}
            value={myBotsSort}
            onChange={(id) => setMyBotsSort(id as BotBoardSortKey)}
          />
        </PrefSection>

        <PrefSection
          label="Org bots sort"
          hint="Default sort for the Position Tracker's org bot section."
        >
          <ChoiceRow
            options={BOT_BOARD_SORT_CHOICES}
            value={orgBotsSort}
            onChange={(id) => setOrgBotsSort(id as BotBoardSortKey)}
          />
        </PrefSection>
      </PrefGroup>
    </>
  );
}

/**
 * One named preference group — #677's organizing unit.
 *
 * The seven groups the issue names are peers, so each is its own card rather
 * than a subheading inside one "Preferences" card. A subheading reads as a
 * subdivision of its parent's label, and "Preferences" is exactly the
 * ambiguous label the acceptance criterion ("grouped and navigable without
 * hiding controls behind ambiguous labels") rules out: a trader hunting for
 * the order-book depth has to guess that a card called Preferences is where
 * Game Detail lives.
 *
 * `id` is a deep-link target and therefore part of this page's contract with
 * the rest of the app, the way `#alerts` already is (`AlertControlsMenu`
 * links to it). Renaming one silently breaks whatever links to it — grep
 * before you do. `tabIndex={-1}` + `scroll-mt-2` mirror the alerts section so
 * `scrollToHashSection` puts a keyboard user INSIDE the group rather than
 * just above it.
 *
 * The storage note repeats on every group deliberately. Each card is its own
 * deep-link target now, so a trader can arrive at one having never seen the
 * top of the page — and "saved locally to this browser" is the fact they need
 * before they change anything in it. Stated once, it is not stated at all for
 * six of the seven arrival paths.
 */
function PrefGroup({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section
      id={id}
      tabIndex={-1}
      className="bg-card border-border scroll-mt-2 rounded-lg border p-5 focus:outline-none"
    >
      <div className="mb-5">
        <h2 className="text-base font-medium">{title}</h2>
        <p className="text-muted-foreground mt-1 text-xs">
          Saved locally to this browser.
        </p>
      </div>

      <div className="space-y-5">{children}</div>
    </section>
  );
}

function PrefSection({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-muted-foreground mb-1.5 block text-xs font-medium">
        {label}
      </label>
      {children}
      {hint && (
        <p className="text-muted-foreground mt-1.5 text-xs leading-snug">{hint}</p>
      )}
    </div>
  );
}

function ChoiceRow<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (id: T) => void;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onChange(opt.id)}
          className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
            value === opt.id
              ? 'bg-blue-500/20 text-blue-600 ring-1 ring-blue-500/40 dark:text-blue-400'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
